import React, { ReactElement, useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { List, Map } from 'immutable';
import { fetchChannelStatSummary } from 'flow/actions/channels-actions';
import FavCheckbox from 'flow/components/FavoriteCheckbox';
import Page from 'flow/components/Setting/Page';
import Loading from 'flow/components/ui-components/Loading';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { PbxParams } from 'flow/types/Params';
import { routes } from 'flow/types/routes';

import {
    getChannelStatisticsFavorites,
    saveChannelStatisticsFavorites,
} from 'flow/utils/browser-utils';
import { twMerge } from 'tailwind-merge';
import PercentageWidget from './statistic-widgets/PercentageWidget';

const intervalLabels = [
    'Queue.Statistics.Interval.Today',
    'Queue.Statistics.Interval.Week',
    'Queue.Statistics.Interval.Month',
];

const intervals = ['today', 'this_week', 'this_month'];

const ChannelStatistics = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { openModal } = useModal();

    const { entityKey } = useParams<PbxParams>();

    const title = i18next.t('Channel.Statistics.Header.Title');
    const description = i18next.t('Queue.Statistics.Header.Description');

    useEffect(() => {
        dispatch(fetchChannelStatSummary(entityKey));
    }, [entityKey]);

    const statSummary = useAppSelector((state) =>
        state.channels.getIn(['channels', entityKey, 'statistics']),
    );

    const [favorites, setFavorites] = useState(
        List(getChannelStatisticsFavorites(entityKey) || ['answeredChats']),
    );

    if (!statSummary) {
        return <Loading />;
    }

    const removeFavorite = (statisticsType: string) => {
        const index = favorites.indexOf(statisticsType);
        const updates = favorites.splice(index, 1);
        saveChannelStatisticsFavorites(entityKey, updates.toJS());
        // @ts-expect-error
        setFavorites(updates);
    };

    const saveFavourite = (statisticsType: string) => {
        const prevChecked = !!favorites?.find(
            (item: string) => item === statisticsType,
        );

        if (favorites.size < 2) {
            if (!prevChecked) {
                const updates = favorites.push(statisticsType);
                saveChannelStatisticsFavorites(entityKey, updates.toJS());
                setFavorites(updates);
            } else {
                removeFavorite(statisticsType);
            }
        } else if (favorites.size === 2 && prevChecked) {
            removeFavorite(statisticsType);
        } else {
            openModal('confirmationDialog', {
                title: i18next.t('Queue.Statistics.MaximumWidgets.Title'),
                description: i18next.t(
                    'Queue.Statistics.MaximumWidgets.Description',
                ),
            });
        }
    };

    const renderAnswerRatio = (index: number) => {
        const intervalStatistics = statSummary?.getIn(
            ['channelStats', intervals[index]],
            Map(),
        );

        return (
            <PercentageWidget
                successfulEvents={intervalStatistics?.get('answeredChats')}
                allEvents={intervalStatistics?.get('initializedChats')}
                strokeColor={getComputedStyle(
                    document.documentElement,
                ).getPropertyValue('--color-channel')}
                trailColor={getComputedStyle(
                    document.documentElement,
                ).getPropertyValue('--color-ornament')}
            />
        );
    };

    const renderServiceLevel = (index: number, time: string) => {
        const intervalStatistics = statSummary?.getIn(
            ['channelStats', intervals[index]],
            Map(),
        );

        return (
            <PercentageWidget
                successfulEvents={intervalStatistics?.getIn([
                    'serviceLevels',
                    `${time}`,
                ])}
                allEvents={intervalStatistics?.get('initializedChats')}
                strokeColor={getComputedStyle(
                    document.documentElement,
                ).getPropertyValue('--color-channel')}
                trailColor={getComputedStyle(
                    document.documentElement,
                ).getPropertyValue('--color-ornament')}
            />
        );
    };

    const renderStatisticsCategory = (
        type: string,
        title: string | ReactElement,
        description: ReactElement,
        renderStatistics: (value: number) => ReactElement,
    ) => {
        // Read straight from the stored favourites on every render. Past the
        // max, saveFavourite only opens the dialog and saves nothing, so this
        // stays false and the star does not move.
        const checked = !!favorites?.find((item: string) => item === type);

        return (
            <div className="flex w-full flex-col items-center justify-center rounded-md border p-3 dark:bg-gray-100">
                <div className="flex w-full">
                    <div className="flex w-full flex-col justify-between gap-y-2">
                        <div className="flex w-full flex-1 items-center justify-center text-xl font-bold">
                            <div className="flex flex-1 justify-center ps-8">
                                {title}
                            </div>

                            <div className="flex justify-end">
                                <FavCheckbox
                                    className={twMerge(
                                        checked ? 'bg-green' : 'bg-gray-200',
                                    )}
                                    color="text-white"
                                    checked={checked}
                                    onChange={() => saveFavourite(type)}
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-center text-base">
                            {description}
                        </div>
                    </div>
                </div>
                <div className="flex w-full flex-wrap justify-evenly gap-2 py-8">
                    {intervalLabels.map((label, index) => (
                        <div className="gap-2" key={index}>
                            <div className="flex justify-center p-1 font-bold text-gray-700">
                                {i18next.t(label)}
                            </div>
                            <div className="flex-end flex">
                                {renderStatistics(index)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const handleBackButton = () => {
        if (
            location.pathname ===
            generatePath(routes['pbx.channels.entityKey.settings.statistics'], {
                entityKey,
            })
        ) {
            return history.push(
                generatePath(routes['pbx.channels.channelKey.settings'], {
                    channelKey: entityKey,
                }),
            );
        }

        return history.push(
            generatePath(routes['pbx.channels.entityKey'], { entityKey }),
        );
    };

    return (
        <Page
            header={i18next.t('Queue.Statistics.Title')}
            onClickBack={handleBackButton}
        >
            <div className="flex flex-col gap-y-4">
                <div className="flex flex-col gap-y-2">
                    <div className="flex items-center justify-center text-xl font-bold">
                        {title}
                    </div>
                    <div className="flex items-center justify-center text-base">
                        {description}
                    </div>
                </div>

                {renderStatisticsCategory(
                    'answeredChats',
                    i18next.t('Queue.Statistics.AnswerRatio.Title'),
                    i18next.t('Channel.Statistics.AnswerRatio.Description'),
                    renderAnswerRatio,
                )}

                {renderStatisticsCategory(
                    'serviceLevels60',
                    i18next.t('Queue.Statistics.ServiceLevel.Title'),
                    i18next.t('Channel.Statistics.ServiceLevel.Description', {
                        minutes: 1,
                    }),
                    (index: number) => renderServiceLevel(index, '60'),
                )}

                {renderStatisticsCategory(
                    'serviceLevels120',
                    i18next.t('Queue.Statistics.ServiceLevel.Title'),
                    i18next.t('Channel.Statistics.ServiceLevel.Description', {
                        minutes: 2,
                    }),
                    (index: number) => renderServiceLevel(index, '120'),
                )}
            </div>
        </Page>
    );
};

export default ChannelStatistics;
